import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProvider } from '~/Hooks';
import { Button } from '~/UIKit';
import { ItemCreationFieldValue } from '~/Components';
import { ExtendedEventSeries } from '../extendedEventSeries.model';
import { EventScheduleTimeSeriesStyles } from './EventScheduleTimeSeries.styles';
import { EventScheduleTimeSeriesItem } from './EventScheduleTimeSeriesItem/EventScheduleTimeSeriesItem';
import { Segment } from '~/Helpers/Segment.helper';
import { EventType } from '~/Models/DTO/Event.model';
import { SegmentName } from '~/Constants/Segment.constants';
import { useDefaultTimeSeries } from './useDefaultTimeSeries';

export interface IEventScheduleTimeSeriesProps {
  eventGroupId?: string;
  setFieldValue?: ItemCreationFieldValue;
  name: string;
  disabled?: boolean;
  defaultSeries?: ExtendedEventSeries[];
  eventType: EventType;
}

export const EventScheduleTimeSeries = ({
  eventType,
  eventGroupId,
  setFieldValue,
  name,
  disabled,
  defaultSeries,
}: IEventScheduleTimeSeriesProps) => {
  const { classes } = EventScheduleTimeSeriesStyles();
  const { t } = useTranslation();
  const { currentProvider, isUsingThePartnerTool } = useProvider();
  const { getDefaultTimeSeries } = useDefaultTimeSeries();

  const [timeSeriesList, setTimeSeriesList] = useState<ExtendedEventSeries[]>(defaultSeries || []);

  const createAnewTimeSeries = () => {
    setTimeSeriesList([...timeSeriesList, getDefaultTimeSeries({ eventType, eventGroupId })]);

    if (!eventGroupId && currentProvider) {
      const { email, domain } = currentProvider;
      Segment.track(SegmentName.CREATE_TIMESLOT, {
        email,
        domain,
        eventType,
        isUsingPartnerPortal: isUsingThePartnerTool,
      });
    }
  };

  const onUpdate = (updateIndex: number) => (timeSeries: ExtendedEventSeries) => {
    setTimeSeriesList([
      ...timeSeriesList.map((t, index) => (index === updateIndex ? timeSeries : t)),
    ]);
  };
  const onUpdateSingleValue = (name: string, value: any, updateIndex: number) => {
    setTimeSeriesList((oldTimeSeriesList) => [
      ...oldTimeSeriesList.map((t, index) => (index === updateIndex ? { ...t, [name]: value } : t)),
    ]);
  };
  const onDelete = (deleteIndex: number, isExist: boolean) => {
    setTimeSeriesList((oldTimeSeriesList) =>
      isExist
        ? [
            ...oldTimeSeriesList.map((t, index) =>
              index === deleteIndex ? { ...t, shouldDelete: !t.shouldDelete } : t,
            ),
          ]
        : [...oldTimeSeriesList.filter((t, index) => index !== deleteIndex)],
    );
  };
  useEffect(() => {
    if (setFieldValue) {
      setFieldValue(name, timeSeriesList);
    }
  }, [timeSeriesList]);

  return (
    <div className={classes.TimeSeriesRoot}>
      {
        <div className={classes.TimeSeriesListContainer}>
          {timeSeriesList.map((timeSeries, index) => (
            <EventScheduleTimeSeriesItem
              eventType={eventType}
              timeSeries={timeSeries}
              onUpdate={onUpdate(index)}
              onUpdateSingleValue={(name: string, value: any) =>
                onUpdateSingleValue(name, value, index)
              }
              disabled={!!disabled || !!timeSeries.shouldDelete}
              onDelete={() => onDelete(index, !!timeSeries.id)}
              key={`SCHEDULE_TIME_SERIES_${index}`}
            />
          ))}
        </div>
      }
      <Button
        size="medium"
        variant="contained"
        className={classes.TimeSeriesCreationButton}
        onClick={createAnewTimeSeries}
        disabled={disabled}
        data-testid="event.addTimeslotButton"
      >
        {t('app.addTimeslot')}
      </Button>
    </div>
  );
};
